//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { SocialNetwork } from '@models/SocialNetwork';
import { Mixins } from '@theme/mixins';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

import {
  FacebookWebLinks,
  InstagramWebLinks,
} from 'app/constants/social-links';

export interface SocialUserLinkProps {
  children?: ReactNode;
  defaultUrl?: string;
  socialNetwork: SocialNetwork;
  username: string;
}

export const SocialUserLink = ({
  socialNetwork,
  username,
  defaultUrl,
  children,
}: SocialUserLinkProps) => {
  const href = (() => {
    if (isDefined(defaultUrl)) {
      return defaultUrl;
    }
    switch (socialNetwork) {
      case SocialNetwork.Instagram:
        return InstagramWebLinks.getUserLink(username);
      case SocialNetwork.Facebook:
        return FacebookWebLinks.getUserLink(username);
      default:
        return undefined;
    }
  })();

  return (
    <>
      {isDefined(href) ? (
        <Link
          href={href}
          target={'_blank'}
          rel='noreferrer'
          trackingId={UIATrackingIds.RIGHTS_OPEN_USER_WEB_LINK}
        >
          <BodyBoldText>{children ?? username}</BodyBoldText>
        </Link>
      ) : (
        <BodyText>{children ?? username}</BodyText>
      )}
    </>
  );
};

const Link = withTracking(styled.a`
  ${Mixins.Text.LinkPrimary}
  display: flex;
  justify-content: flex-start;
`);
